import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { navigate } from '../App'
import { colors } from '../theme'
import {
  enrollContacts,
  listSequences,
  prospectPeople,
  type ProspectCandidate,
  type ProspectResponse,
  type Sequence,
} from '../api/sequences'

const SAMPLE_PROMPTS = [
  'Independent boutique hotels (10-50 rooms) in the US Northeast with a surface lot.',
  'Property managers in Florida running 3+ buildings, using Flowbird or T2 today.',
  'Marina general managers in coastal US markets — high seasonal overflow parking.',
  'Restaurant ops directors at multi-location concepts in Texas, valet or surface.',
]

export default function Prospect() {
  const { authHeaders, apiBase } = useAuth()
  const [prompt, setPrompt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ProspectResponse | null>(null)
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [sequences, setSequences] = useState<Sequence[]>([])
  const [enrollSequenceId, setEnrollSequenceId] = useState<string>('')
  const [enrolling, setEnrolling] = useState(false)
  const [enrollMessage, setEnrollMessage] = useState<string | null>(null)

  useEffect(() => {
    listSequences(apiBase, authHeaders(), { mine: true })
      .then((rows) => {
        setSequences(rows)
        const active = rows.find((s) => s.status === 'active')
        if (active) setEnrollSequenceId(active.id)
      })
      .catch(() => {
        // Non-fatal; the rep can still search.
      })
  }, [apiBase, authHeaders])

  async function onSearch() {
    setBusy(true)
    setError(null)
    setResult(null)
    setSelected({})
    setEnrollMessage(null)
    try {
      const res = await prospectPeople(apiBase, authHeaders(), prompt)
      setResult(res)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed')
    } finally {
      setBusy(false)
    }
  }

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected],
  )

  async function onEnroll() {
    if (!enrollSequenceId || !result) return
    const picks = result.candidates.filter((c) => selected[c.apollo_id])
    if (picks.length === 0) return
    setEnrolling(true)
    setError(null)
    setEnrollMessage(null)
    try {
      const res = await enrollContacts(apiBase, authHeaders(), enrollSequenceId, {
        contacts: picks,
      })
      // Build a precise summary so the rep knows WHY contacts dropped
      // out instead of seeing the generic "skipped N without an email"
      // mystery. Apollo prospects can fail enrollment for two reasons:
      // bulk_match didn't find an email at all, or the email is on
      // Apollo's "extrapolated/guessed/unavailable" list (we only
      // enroll Apollo contacts whose email_status is "verified").
      const parts: string[] = []
      parts.push(
        `Enrolled ${res.enrolled_count} contact${res.enrolled_count === 1 ? '' : 's'}`,
      )
      if (res.skipped_no_email_count) {
        parts.push(
          `skipped ${res.skipped_no_email_count} with no email on file`,
        )
      }
      if (res.skipped_unverified_count) {
        const detail =
          res.skipped_unverified_names.length > 0
            ? ` (${res.skipped_unverified_names.slice(0, 3).join('; ')}${res.skipped_unverified_names.length > 3 ? '…' : ''})`
            : ''
        parts.push(
          `skipped ${res.skipped_unverified_count} unverified${detail}`,
        )
      }
      setEnrollMessage(parts.join(' · '))
      setSelected({})
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enrollment failed')
    } finally {
      setEnrolling(false)
    }
  }

  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '2.5px',
          color: colors.purple,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        Apollo — through prompting
      </div>
      <h1
        style={{
          fontSize: 32,
          fontWeight: 700,
          letterSpacing: '-1px',
          margin: 0,
          color: colors.black,
        }}
      >
        Find prospects
      </h1>
      <p
        style={{
          color: colors.textMuted,
          maxWidth: 720,
          marginTop: 8,
          marginBottom: 24,
          lineHeight: 1.6,
          fontSize: 14,
        }}
      >
        Describe who you want to reach. The portal expands it into Apollo
        filters, runs the search, and brings back candidate contacts.
        Apollo obfuscates last names + emails in search results; real
        contact data is revealed automatically when you enroll a contact
        in a sequence (costs 1 Apollo credit per enriched contact).
      </p>

      <div
        style={{
          background: colors.white,
          border: `1px solid ${colors.divider}`,
          borderRadius: 12,
          padding: 20,
          marginBottom: 20,
        }}
      >
        <label
          style={{
            display: 'block',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '2px',
            color: colors.purple,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Who do you want to reach?
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          placeholder="e.g. boutique hotel GMs in coastal New England with an attached surface lot"
          style={{
            display: 'block',
            width: '100%',
            padding: '12px 14px',
            fontSize: 14,
            fontFamily: 'inherit',
            border: `1px solid ${colors.divider}`,
            borderRadius: 10,
            background: colors.white,
            color: colors.black,
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {SAMPLE_PROMPTS.map((p) => (
            <button
              key={p}
              onClick={() => setPrompt(p)}
              style={{
                background: colors.bgAlt,
                border: `1px solid ${colors.divider}`,
                color: colors.textMuted,
                borderRadius: 999,
                padding: '4px 10px',
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              {p.length > 65 ? p.slice(0, 64) + '…' : p}
            </button>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            marginTop: 14,
          }}
        >
          <button
            disabled={busy || prompt.trim().length < 4}
            onClick={onSearch}
            style={{
              background: colors.purple,
              color: colors.white,
              border: 'none',
              borderRadius: 10,
              padding: '10px 18px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              cursor:
                busy || prompt.trim().length < 4 ? 'not-allowed' : 'pointer',
              opacity: busy || prompt.trim().length < 4 ? 0.5 : 1,
            }}
          >
            {busy ? 'Searching…' : 'Search Apollo'}
          </button>
        </div>
      </div>

      {error ? (
        <div
          style={{
            padding: '12px 16px',
            background: colors.warningBg,
            border: `1.5px solid ${colors.warningBorder}`,
            borderRadius: 10,
            fontSize: 13,
            color: colors.black,
            marginBottom: 16,
          }}
        >
          <strong style={{ color: colors.red }}>Error:</strong> {error}
        </div>
      ) : null}

      {enrollMessage ? (
        <div
          style={{
            padding: '12px 16px',
            background: colors.purpleSoft,
            border: `1px solid ${colors.purpleSoftHi}`,
            borderRadius: 10,
            fontSize: 13,
            color: colors.purple,
            marginBottom: 16,
            fontWeight: 700,
          }}
        >
          {enrollMessage}
        </div>
      ) : null}

      {result ? (
        <Results
          result={result}
          selected={selected}
          setSelected={setSelected}
          sequences={sequences}
          enrollSequenceId={enrollSequenceId}
          setEnrollSequenceId={setEnrollSequenceId}
          selectedCount={selectedCount}
          enrolling={enrolling}
          onEnroll={onEnroll}
        />
      ) : null}
    </div>
  )
}

function Results(props: {
  result: ProspectResponse
  selected: Record<string, boolean>
  setSelected: (v: Record<string, boolean>) => void
  sequences: Sequence[]
  enrollSequenceId: string
  setEnrollSequenceId: (v: string) => void
  selectedCount: number
  enrolling: boolean
  onEnroll: () => void
}) {
  const {
    result,
    selected,
    setSelected,
    sequences,
    enrollSequenceId,
    setEnrollSequenceId,
    selectedCount,
    enrolling,
    onEnroll,
  } = props
  const ids = result.candidates.map((c) => c.apollo_id)
  const allSelected = ids.length > 0 && ids.every((id) => selected[id])

  function toggleAll() {
    if (allSelected) setSelected({})
    else {
      const next: Record<string, boolean> = {}
      for (const id of ids) next[id] = true
      setSelected(next)
    }
  }

  return (
    <div>
      <div
        style={{
          background: colors.bgAlt,
          border: `1px solid ${colors.divider}`,
          borderRadius: 10,
          padding: '12px 14px',
          fontSize: 12,
          color: colors.black,
          marginBottom: 14,
          lineHeight: 1.5,
        }}
      >
        <strong style={{ color: colors.purple }}>Why these:</strong>{' '}
        {result.filter_rationale}
        <span
          style={{ marginLeft: 8, color: colors.textMuted, fontSize: 11 }}
        >
          · {result.provider} · {result.model} · returned{' '}
          {result.candidates.length} of{' '}
          {result.pagination.total_entries ?? '?'} total
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <button
          onClick={toggleAll}
          style={{
            background: colors.white,
            border: `1px solid ${colors.divider}`,
            color: colors.black,
            borderRadius: 8,
            padding: '6px 12px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          {allSelected ? 'Clear selection' : 'Select all'}
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ color: colors.textMuted, fontSize: 12 }}>
          {selectedCount} selected
        </div>
        <select
          value={enrollSequenceId}
          onChange={(e) => setEnrollSequenceId(e.target.value)}
          style={{
            padding: '8px 10px',
            fontSize: 13,
            border: `1px solid ${colors.divider}`,
            borderRadius: 8,
            background: colors.white,
            color: colors.black,
            minWidth: 220,
          }}
        >
          <option value="">— pick a sequence —</option>
          {sequences.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.status})
            </option>
          ))}
        </select>
        <button
          disabled={
            enrolling || selectedCount === 0 || !enrollSequenceId
          }
          onClick={onEnroll}
          style={{
            background: colors.purple,
            color: colors.white,
            border: 'none',
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            cursor:
              enrolling || selectedCount === 0 || !enrollSequenceId
                ? 'not-allowed'
                : 'pointer',
            opacity:
              enrolling || selectedCount === 0 || !enrollSequenceId
                ? 0.5
                : 1,
          }}
        >
          {enrolling
            ? 'Enrolling…'
            : `Enroll${selectedCount ? ` (${selectedCount})` : ''}`}
        </button>
      </div>

      <div
        style={{
          background: colors.white,
          border: `1px solid ${colors.divider}`,
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        {result.candidates.length === 0 ? (
          <div
            style={{
              padding: 30,
              textAlign: 'center',
              color: colors.textMuted,
            }}
          >
            No candidates returned — try a broader prompt.
          </div>
        ) : (
          result.candidates.map((c, i) => (
            <CandidateRow
              key={c.apollo_id || `${c.full_name}-${i}`}
              candidate={c}
              checked={!!selected[c.apollo_id]}
              onToggle={() =>
                setSelected({ ...selected, [c.apollo_id]: !selected[c.apollo_id] })
              }
              last={i === result.candidates.length - 1}
            />
          ))
        )}
      </div>

      {sequences.length === 0 ? (
        <div
          style={{
            marginTop: 16,
            textAlign: 'center',
            color: colors.textMuted,
            fontSize: 13,
          }}
        >
          No sequences yet — {' '}
          <button
            onClick={() => navigate({ name: 'sequence-new' })}
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.purple,
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            create one
          </button>{' '}
          first, then come back to enroll.
        </div>
      ) : null}
    </div>
  )
}

function CandidateRow({
  candidate,
  checked,
  onToggle,
  last,
}: {
  candidate: ProspectCandidate
  checked: boolean
  onToggle: () => void
  last: boolean
}) {
  const c = candidate
  const loc = [c.city, c.state, c.country].filter(Boolean).join(', ')
  const enrich = c.enrichment as
    | {
        seniority?: string
        company_employees?: number
        company_short_description?: string
        headline?: string
      }
    | undefined
  return (
    <label
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1.4fr 1.4fr 1fr 0.8fr',
        gap: 16,
        padding: '14px 18px',
        borderBottom: last ? 'none' : `1px solid ${colors.divider}`,
        alignItems: 'center',
        cursor: 'pointer',
        background: checked ? colors.purpleSoft : colors.white,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        style={{ accentColor: colors.purple, width: 18, height: 18 }}
      />
      <div>
        <div style={{ fontWeight: 700, color: colors.black, fontSize: 14 }}>
          {c.full_name || `${c.first_name} ${c.last_name}`.trim() || '—'}
        </div>
        <div
          style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}
        >
          {c.title || enrich?.headline || '—'}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 13, color: colors.black }}>
          {c.company || '—'}
        </div>
        <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
          {c.industry}
          {enrich?.company_employees ? ` · ~${enrich.company_employees}` : ''}
        </div>
      </div>
      <div style={{ color: colors.textMuted, fontSize: 12 }}>
        {loc || '—'}
      </div>
      <div style={{ fontSize: 11, color: colors.textMuted, textAlign: 'right' }}>
        {/* Apollo's api_search response hides email until enrichment
            runs (at enroll time). has_email tells us whether the
            bulk_match call will reveal one. */}
        {c.email ? (
          <span style={{ color: colors.purple, fontWeight: 700 }}>
            {c.email}
          </span>
        ) : c.has_email ? (
          <span style={{ color: colors.purple, fontWeight: 700 }}>
            email on enroll
          </span>
        ) : (
          <span>no email</span>
        )}
        {c.linkedin_url ? (
          <div>
            <a
              href={c.linkedin_url}
              target="_blank"
              rel="noreferrer"
              style={{
                color: colors.blue,
                fontSize: 11,
                textDecoration: 'none',
              }}
            >
              LinkedIn ↗
            </a>
          </div>
        ) : null}
      </div>
    </label>
  )
}
